"use client"

import { useState, useEffect } from "react"
import { useSession } from "next-auth/react"
import { ThumbsUp, AlertCircle, CheckCircle2, Clock, ExternalLink } from "lucide-react"

interface VoteLink {
    id: number
    name: string
    url: string
    image_url: string | null
    reward_item_id: number | null
    reward_item_count: number | null
    reward_item_name: string | null
    cooldown_hours: number
    active: boolean
    canVote: boolean
    cooldownRemaining: number // in seconds
    is_dynamic?: boolean
    api_server_id?: string
    hasUnverifiedExternalVote?: boolean
}

export default function VotePage() {
    const { data: session } = useSession()
    const [links, setLinks] = useState<VoteLink[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [pendingRewards, setPendingRewards] = useState<any[]>([])
    const [accounts, setAccounts] = useState<any[]>([])
    const [isClaiming, setIsClaiming] = useState(false)

    useEffect(() => {
        fetchVoteData()
        if (session) {
            fetchPendingRewards()
            fetchCharacters()
        }
    }, [session])

    // Decrement cooldowns every second
    useEffect(() => {
        const interval = setInterval(() => {
            setLinks(currentLinks => 
                currentLinks.map(link => {
                    if (link.cooldownRemaining > 0) {
                        const newCooldown = link.cooldownRemaining - 1
                        return {
                            ...link,
                            cooldownRemaining: newCooldown,
                            canVote: newCooldown <= 0
                        }
                    }
                    return link
                })
            )
        }, 1000)

        return () => clearInterval(interval)
    }, [])

    const fetchVoteData = async () => {
        try {
            const res = await fetch("/api/vote")
            const data = await res.json()
            setLinks(data.links || [])
        } catch (error) {
            console.error("Failed to fetch vote data:", error)
        } finally {
            setIsLoading(false)
        }
    }

    const fetchPendingRewards = async () => {
        try {
            const res = await fetch("/api/vote/claim")
            const data = await res.json()
            setPendingRewards(data.rewards || [])
        } catch (error) {
            console.error("Failed to fetch pending rewards:", error)
        }
    }

    const fetchCharacters = async () => {
        try {
            const res = await fetch("/api/dashboard/characters")
            const data = await res.json()
            setAccounts(data.accounts || [])
        } catch (error) {
            console.error("Failed to fetch characters:", error)
        }
    }

    const handleClaim = async (rewardId: number, charId: number, serverId: string) => {
        if (!charId) {
            alert("Please select a character")
            return
        }
        setIsClaiming(true)
        try {
            const res = await fetch("/api/vote/claim", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rewardId, charId, serverId })
            })
            const data = await res.json()
            if (res.ok) {
                alert("Reward claimed successfully!")
                fetchPendingRewards()
            } else {
                alert(data.error || "Failed to claim reward")
            }
        } catch (error) {
            console.error("Claim error:", error)
            alert("An error occurred while claiming")
        } finally {
            setIsClaiming(false)
        }
    }

    const formatTime = (seconds: number) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor((seconds % 3600) / 60)
        const s = seconds % 60
        return `${h}h ${m}m ${s}s`
    }

    // Replace placeholders in URL (handles both {username} and PLAYER_ID template formats)
    const getVoteUrl = (link: VoteLink) => {
        if (!session?.user?.login) return link.url
        return link.url
            .replace("{username}", session.user.login)
            .replace("PLAYER_ID", session.user.login)
    }

    const handleVerifyClick = async (link: VoteLink) => {
        setIsClaiming(true)
        try {
            const res = await fetch("/api/vote/verify", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ linkId: link.id })
            })
            const data = await res.json()
            if (res.ok) {
                alert(data.message || "Vote verified successfully! Reward added to your pending rewards.")
                fetchVoteData()
                fetchPendingRewards()
            } else {
                alert(data.error || "Failed to verify vote.")
            }
        } catch (error) {
            console.error("Verification error:", error)
            alert("An error occurred while verifying your vote.")
        } finally {
            setIsClaiming(false)
        }
    }

    const handleVoteClick = async (link: VoteLink) => {
        if (!session?.user?.login) {
            alert("Please log in to receive rewards for voting!")
            return
        }

        if (!link.canVote) {
            alert(`You must wait ${formatTime(link.cooldownRemaining)} before voting here again.`)
            return
        }

        if (link.is_dynamic && link.api_server_id !== "l2votes" && link.api_server_id !== "hotservers" && link.api_server_id !== "hopzonenet") {
            try {
                const res = await fetch("/api/vote/generate", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ linkId: link.id })
                })
                const data = await res.json()
                if (data.url) {
                    window.open(data.url, "_blank")
                } else {
                    alert(data.error || "Failed to generate vote link. Please try again later.")
                }
            } catch (error) {
                console.error("Failed to generate link:", error)
                alert("An error occurred. Please try again.")
            }
        } else {
            window.open(getVoteUrl(link), "_blank")
        }
    }

    return (
        <div className="min-h-screen bg-transparent pb-20 pt-32">
            <div className="mx-auto max-w-5xl px-4">
                <div className="mb-12 text-center">
                    <h1 className="mb-4 text-4xl font-black tracking-tight text-white md:text-5xl lg:text-6xl uppercase">
                        Vote for <span className="text-primary drop-shadow-[0_0_15px_rgba(234,179,8,0.5)]">DragonL2</span>
                    </h1>
                    <p className="mx-auto max-w-2xl text-lg text-slate-400">
                        Support the server by voting on our top ranking sites! 
                        You will be rewarded with exclusive in-game items for every successful vote.
                    </p>
                </div>

                {session && pendingRewards.length > 0 && (
                    <div className="mb-12 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6 backdrop-blur-sm">
                        <h2 className="mb-4 text-xl font-bold text-white flex items-center gap-2">
                            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                            Pending Vote Rewards
                        </h2>
                        <div className="space-y-4">
                            {pendingRewards.map(reward => (
                                <div key={reward.id} className="flex flex-col md:flex-row items-center justify-between gap-4 rounded-xl border border-white/10 bg-black/40 p-4">
                                    <div>
                                        <p className="font-bold text-white">
                                            {reward.reward_item_count}x {reward.reward_item_name || `Item ID: ${reward.reward_item_id}`}
                                        </p>
                                        <p className="text-sm text-slate-400">Vote Date: {new Date(reward.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="flex w-full md:w-auto items-center gap-2">
                                        <select 
                                            id={`char-select-${reward.id}`}
                                            className="w-full md:w-64 rounded-lg border border-white/10 bg-black/50 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none"
                                            defaultValue=""
                                        >
                                            <option value="" disabled>Select Character...</option>
                                            {accounts.map(acc => (
                                                <optgroup key={acc.login} label={`[${acc.server_id}] ${acc.login}`}>
                                                    {acc.characters.map((char: any) => (
                                                        <option key={char.charId} value={`${char.charId}|${acc.server_id}`}>
                                                            {char.char_name} (Lv {char.level})
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                        <button
                                            disabled={isClaiming}
                                            onClick={() => {
                                                const select = document.getElementById(`char-select-${reward.id}`) as HTMLSelectElement;
                                                if (select && select.value) {
                                                    const [charId, serverId] = select.value.split('|');
                                                    handleClaim(reward.id, parseInt(charId), serverId);
                                                } else {
                                                    alert("Please select a character");
                                                }
                                            }}
                                            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-500 disabled:opacity-50"
                                        >
                                            Claim
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {!session && (
                    <div className="mb-8 flex items-center justify-center gap-3 rounded-xl border border-yellow-500/20 bg-yellow-500/10 p-4 text-yellow-500 backdrop-blur-sm">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        <p className="text-sm font-medium">You must be logged in to receive vote rewards!</p>
                    </div>
                )}

                {isLoading ? (
                    <div className="text-center text-slate-500 py-20">Loading vote links...</div>
                ) : links.length === 0 ? (
                    <div className="text-center text-slate-500 py-20 bg-white/5 rounded-2xl border border-white/10">
                        No active vote links available at this time.
                    </div>
                ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {links.map((link) => (
                            <div 
                                key={link.id} 
                                className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-black/40 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl ${
                                    session && link.canVote 
                                        ? "border-emerald-500/30 hover:border-emerald-500/60 shadow-emerald-500/10" 
                                        : "border-white/10 hover:border-white/20"
                                }`}
                            >
                                {/* Banner Image or Placeholder */}
                                <div className="flex h-32 items-center justify-center bg-slate-900 border-b border-white/5">
                                    {link.image_url ? (
                                        <img 
                                            src={link.image_url} 
                                            alt={link.name} 
                                            className="max-h-full max-w-full object-contain p-2"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="flex flex-col items-center gap-2 text-slate-500">
                                            <ThumbsUp className="h-8 w-8" />
                                            <span className="font-bold uppercase tracking-widest text-xs">{link.name}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex flex-1 flex-col p-5">
                                    <h3 className="mb-1 text-xl font-bold text-white">{link.name}</h3>
                                    
                                    <div className="mb-6 flex items-center gap-2">
                                        <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-xs font-bold text-emerald-400 border border-emerald-500/30">
                                            {link.reward_item_count}x {link.reward_item_name || "Item"}
                                        </span>
                                        <span className="text-xs text-slate-400">
                                            Every {link.cooldown_hours}h
                                        </span>
                                    </div>

                                    <div className="mt-auto">
                                        {!session ? (
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/10 py-3 text-sm font-bold text-white transition-colors hover:bg-white/20"
                                            >
                                                Vote Anonymously <ExternalLink className="h-4 w-4" />
                                            </a>
                                        ) : link.canVote ? (
                                            (link.api_server_id === "l2votes" || link.api_server_id === "hopzonenet" || link.api_server_id === "hotservers" || link.api_server_id === "74" || !link.is_dynamic) ? (
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleVoteClick(link)}
                                                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white transition-all hover:bg-emerald-500 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                                                    >
                                                        <ThumbsUp className="h-4 w-4" /> Vote
                                                    </button>
                                                    <button
                                                        onClick={() => handleVerifyClick(link)}
                                                        disabled={isClaiming}
                                                        className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition-all hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] disabled:opacity-50"
                                                    >
                                                        <CheckCircle2 className="h-4 w-4" /> Verify
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleVoteClick(link)}
                                                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-bold text-white transition-all hover:bg-emerald-500 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]"
                                                >
                                                    <ThumbsUp className="h-4 w-4" /> Vote Now
                                                </button>
                                            )
                                        ) : link.hasUnverifiedExternalVote ? (
                                            <div className="flex gap-2">
                                                <button
                                                    disabled
                                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-emerald-600/50 py-3 text-sm font-bold text-white cursor-not-allowed"
                                                >
                                                    <ThumbsUp className="h-4 w-4" /> Voted
                                                </button>
                                                <button
                                                    onClick={() => handleVerifyClick(link)}
                                                    disabled={isClaiming}
                                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition-all hover:bg-blue-500 hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] disabled:opacity-50"
                                                >
                                                    <CheckCircle2 className="h-4 w-4" /> Verify
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                disabled
                                                className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-800 py-3 text-sm font-bold text-slate-400 cursor-not-allowed"
                                            >
                                                <Clock className="h-4 w-4" /> 
                                                {formatTime(link.cooldownRemaining)}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Status Indicator */}
                                {session && link.canVote && (
                                    <div className="absolute right-3 top-3 rounded-full bg-emerald-500/20 p-1 text-emerald-500 backdrop-blur-md">
                                        <CheckCircle2 className="h-4 w-4" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
